import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { GoalForm } from './GoalForm';
import { useUpdateGoal } from '@/shared/hooks';
import { type CreateGoalFormData } from './types/goals.types';
import { IssueVisibility } from '@momentum/types';
import { buildChangedFields } from '@/shared/utils/form-change-utils';
import type { FullIssueWithChildren } from '@/shared/utils/issue-guards';

interface UpdateGoalModalProps {
  goal: FullIssueWithChildren;
}

export const UpdateGoalModal: React.FC<UpdateGoalModalProps> = ({ goal }) => {
  const { t } = useTranslation('goals');
  const [open, setOpen] = useState(false);
  const updateGoalMutation = useUpdateGoal();

  const initialValues: CreateGoalFormData = {
    ...goal,
    assignees: goal.assignees.map((assignee) => assignee._id),
    creator: goal.issueCreator._id,
    keyResults: goal.keyResults ?? [],
    viewers: goal.viewers
      ? {
          users: goal.viewers.users.map((viewer) => viewer._id) ?? [],
          teams: goal.viewers.teams.map((team) => team._id) ?? [],
        }
      : { users: [], teams: [] },
    visibility: goal.visibility || IssueVisibility.PUBLIC,
  };

  const handleSubmit = async (formData: CreateGoalFormData) => {
    // Only send fields that have changed (PATCH, not PUT)
    const changedFields = buildChangedFields([
      { key: 'title', newValue: formData.title, oldValue: initialValues.title },
      {
        key: 'description',
        newValue: formData.description,
        oldValue: initialValues.description,
      },
      {
        key: 'assignees',
        newValue: formData.assignees,
        oldValue: initialValues.assignees,
        deepCompare: true,
      },
      {
        key: 'creator',
        newValue: formData.creator,
        oldValue: initialValues.creator,
      },
      {
        key: 'status',
        newValue: formData.status,
        oldValue: initialValues.status,
      },
      {
        key: 'startDate',
        newValue: formData.startDate,
        oldValue: initialValues.startDate,
      },
      {
        key: 'dueDate',
        newValue: formData.dueDate,
        oldValue: initialValues.dueDate,
      },
      {
        key: 'parent',
        newValue: formData.parent,
        oldValue: initialValues.parent,
      },
      { key: 'link', newValue: formData.link, oldValue: initialValues.link },
      {
        key: 'collaborators',
        newValue: formData.collaborators,
        oldValue: initialValues.collaborators,
        deepCompare: true,
      },
      {
        key: 'keyResults',
        newValue: formData.keyResults,
        oldValue: initialValues.keyResults,
        deepCompare: true,
      },
      {
        key: 'viewers',
        newValue: formData.viewers,
        oldValue: initialValues.viewers,
        deepCompare: true,
      },
      {
        key: 'visibility',
        newValue: formData.visibility,
        oldValue: initialValues.visibility,
      },
    ]);

    // Only send the request if there are changes
    if (Object.keys(changedFields).length > 0) {
      await updateGoalMutation.mutateAsync({
        id: goal._id,
        data: changedFields,
      });
    }

    // Close modal on success
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        size="lg"
        className="flex flex-col max-h-[90vh] p-0"
        dismissibleByOutsideClick={false}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl font-bold">
            {t('updateGoalTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 px-6 py-4">
          <GoalForm
            initialValues={initialValues}
            isPending={updateGoalMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={t('updateGoal')}
            submittingLabel={t('updating')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
